import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  Search,
  Phone,
  Mail,
  ArrowRight,
  Users,
  Building2,
  Briefcase,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';

const BUILDER_STATUS_COLORS = {
  tendering:  'bg-blue-100 text-blue-700',
  quoted:     'bg-purple-100 text-purple-700',
  likely:     'bg-emerald-100 text-emerald-700',
  won:        'bg-green-100 text-green-700',
  lost:       'bg-red-100 text-red-700',
  'on-hold':  'bg-yellow-100 text-yellow-700',
};

const OPP_STATUS_COLORS = {
  qualification: 'bg-blue-100 text-blue-700',
  proposal:      'bg-purple-100 text-purple-700',
  negotiation:   'bg-amber-100 text-amber-700',
  'closed-won':  'bg-green-100 text-green-700',
  'closed-lost': 'bg-red-100 text-red-700',
};

function SmallBadge({ label, colorClass }) {
  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize', colorClass || 'bg-gray-100 text-gray-700')}>
      {label}
    </span>
  );
}

export function ContactsModule() {
  const { state, actions } = useApp();
  const { opportunities } = state;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContactId, setSelectedContactId] = useState(null);

  // Derive flat list of all contacts enriched with builder + opportunity context
  const allContacts = useMemo(() =>
    opportunities.flatMap(opp =>
      (opp.builders || []).flatMap(b =>
        (b.contacts || []).map(c => ({
          ...c,
          builderName: b.name,
          builderId: b.id,
          builderStatus: b.status,
          opportunityName: opp.name,
          opportunityId: opp.id,
          opportunityStatus: opp.status,
        }))
      )
    ),
    [opportunities]
  );

  const normalizedQuery = searchQuery.toLowerCase();
  const filteredContacts = useMemo(() =>
    allContacts.filter(c =>
      (c.name || '').toLowerCase().includes(normalizedQuery) ||
      (c.email || '').toLowerCase().includes(normalizedQuery) ||
      (c.phone || '').toLowerCase().includes(normalizedQuery) ||
      (c.role || '').toLowerCase().includes(normalizedQuery) ||
      c.builderName.toLowerCase().includes(normalizedQuery) ||
      c.opportunityName.toLowerCase().includes(normalizedQuery)
    ),
    [allContacts, normalizedQuery]
  );

  // Use a composite key (contactId + builderId) because the same contact id
  // could theoretically appear across builders if data is cloned.
  const selectedContact = selectedContactId
    ? allContacts.find(c => `${c.id}-${c.builderId}` === selectedContactId) || null
    : null;

  const handleViewInCRM = (contact) => {
    actions.setCurrentOpportunity(contact.opportunityId);
    actions.setCurrentBuilder(contact.builderId);
    actions.setActiveModule('crm');
  };

  return (
    <div className="h-full flex" data-testid="contacts-module">

      {/* Left: Contact list */}
      <div className="w-72 border-r border-border flex flex-col bg-white shrink-0">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-base mb-3">All Contacts</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filteredContacts.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                {allContacts.length === 0 ? 'No contacts yet' : 'No results'}
              </div>
            ) : (
              filteredContacts.map(contact => {
                const key = `${contact.id}-${contact.builderId}`;
                return (
                  <button
                    key={key}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors',
                      selectedContactId === key && 'bg-accent'
                    )}
                    onClick={() => setSelectedContactId(key)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                        {(contact.name || '?')[0].toUpperCase()}
                      </div>
                      <span className="text-sm font-medium truncate">{contact.name || 'Unnamed'}</span>
                    </div>
                    {contact.role && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5 pl-8">{contact.role}</p>
                    )}
                    <p className="text-xs text-muted-foreground/70 truncate italic pl-8">{contact.builderName}</p>
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right: Contact detail */}
      {selectedContact ? (
        <div className="flex-1 overflow-auto p-5 bg-muted/20">
          <div className="max-w-lg space-y-4">

            {/* Contact card */}
            <div className="bg-white border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-semibold shrink-0">
                  {(selectedContact.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <h2 className="text-base font-semibold">{selectedContact.name || 'Unnamed'}</h2>
                  {selectedContact.role && (
                    <p className="text-sm text-muted-foreground">{selectedContact.role}</p>
                  )}
                </div>
              </div>
              {(selectedContact.email || selectedContact.phone) && (
                <div className="flex flex-wrap gap-4 pt-3 border-t border-border">
                  {selectedContact.email && (
                    <a
                      href={`mailto:${selectedContact.email}`}
                      className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <Mail className="w-4 h-4" />
                      {selectedContact.email}
                    </a>
                  )}
                  {selectedContact.phone && (
                    <a
                      href={`tel:${selectedContact.phone}`}
                      className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <Phone className="w-4 h-4" />
                      {selectedContact.phone}
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Builder context */}
            <div className="bg-white border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Builder</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{selectedContact.builderName}</span>
                    <SmallBadge
                      label={selectedContact.builderStatus}
                      colorClass={BUILDER_STATUS_COLORS[selectedContact.builderStatus]}
                    />
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleViewInCRM(selectedContact)}>
                  View in CRM
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Opportunity context */}
            <div className="bg-white border border-border rounded-lg p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Opportunity</p>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{selectedContact.opportunityName}</span>
                <SmallBadge
                  label={selectedContact.opportunityStatus}
                  colorClass={OPP_STATUS_COLORS[selectedContact.opportunityStatus]}
                />
              </div>
            </div>

          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a contact to view details</p>
          </div>
        </div>
      )}
    </div>
  );
}
